import { Link } from 'react-router-dom'
import { ListaDeDados, type Dado } from '../../../components/ListaDeDados'
import { dataHora } from '../../../lib/datas'
import { TERMOS, TEXTOS } from '../../../lib/glossario'
import { enderecoDoChamadoNoENiteroi } from '../eNiteroi'
import type { DetalheChamado } from '../../../types/chamado'
import { competenciaPorExtenso } from '../competencia'
import { EtiquetaDeOrigemDaDemanda, EtiquetaDeProcedencia, EtiquetaDeSla } from '../etiquetas'

/**
 * A identificação do chamado, fixa no topo.
 *
 * O número do e-Niterói é o `<h1>`, e não o assunto: é por ele que o chamado é citado no termo de
 * recebimento, procurado na planilha e conferido contra o e-Niterói. O assunto vem logo abaixo,
 * porque é o que diz do que se trata.
 *
 * A situação de prazo vem acompanhada da ressalva quando ela é provisória. Chamado ainda aberto tem
 * a situação medida contra o relógio deste instante: "dentro do prazo" hoje pode ser "prazo
 * estourado" amanhã sem que ninguém mexa em nada, e uma tela que não diz isso vira uma captura de
 * tela que alguém anexa ao processo como se fosse um fato estável.
 *
 * @param props o chamado
 * @returns o cabeçalho de identificação
 */
export function CabecalhoDoChamado({ chamado }: { chamado: DetalheChamado }) {
  const identificacao: Dado[] = [
    { rotulo: 'Entidade', valor: chamado.entidade },
    { rotulo: 'Grupo atendente', valor: chamado.grupo },
    { rotulo: TERMOS.competencia.rotulo, valor: competenciaDoChamado(chamado) },
    { rotulo: 'Dado referente a', valor: dataHora(chamado.coletadoEm) },
    { rotulo: 'Solicitação de mudança vinculada', valor: <GmudDoChamado chamado={chamado} /> },
  ]

  return (
    <header className="cartao space-y-4 p-5">
      <div className="min-w-0 space-y-2">
        <h1 className="titulo-da-pagina flex flex-wrap items-baseline gap-3 tabular-nums">
          {chamado.idExterno}
          {/* O fiscal julga conformidade e aceite lendo o atendimento — que fica no e-Niterói. Sem o
              link ele copiava o número, abria outra aba e colava na busca, cem vezes por mês. */}
          {chamado.idExterno && (
            <a
              href={enderecoDoChamadoNoENiteroi(chamado.idExterno)}
              target="_blank"
              rel="noreferrer"
              title={TEXTOS.eNiteroi.abrirAjuda}
              className="text-sm font-medium text-niteroi-700 underline underline-offset-2 hover:text-niteroi-900"
            >
              {TEXTOS.eNiteroi.abrir}
            </a>
          )}
        </h1>
        <p className="max-w-prose text-base text-slate-800">{chamado.titulo}</p>
        <div className="flex flex-wrap items-center gap-2">
          <EtiquetaDeSla situacao={chamado.situacaoSla} rotulo={chamado.situacaoSlaRotulo} />
          <EtiquetaDeOrigemDaDemanda origem={chamado.origemDemanda} />
          <EtiquetaDeProcedencia origem={chamado.origem} rotulo={chamado.origemRotulo} />
        </div>
        {chamado.situacaoSlaProvisoria && (
          <p className="max-w-prose text-sm text-slate-600">
            Este chamado ainda está aberto: a situação acima foi medida agora, contra o relógio, e pode mudar sem
            que ninguém altere o registro.
          </p>
        )}
      </div>

      <ListaDeDados dados={identificacao} />
    </header>
  )
}

/**
 * O mês de referência, ou a explicação de por que ele ainda não existe.
 *
 * Chamado aberto não tem competência, e isso é definição e não ausência de dado: a competência é o
 * mês do FECHAMENTO. Deixar o campo vazio faria parecer que alguém esqueceu de preencher.
 */
function competenciaDoChamado(chamado: DetalheChamado) {
  if (!chamado.competencia) {
    return <span className="text-slate-500">Ainda não tem — o mês de referência é o do fechamento</span>
  }
  return competenciaPorExtenso(chamado.competencia)
}

/**
 * O caminho para a solicitação de mudança vinculada.
 *
 * Aqui há link, ao contrário da listagem: o detalhe recebe o identificador da GMUD junto do código,
 * e é ele que forma o endereço. É a checagem que o fiscal fez à mão em maio — "GMUD não foi anexada
 * ao chamado", anotado duas vezes na planilha.
 */
function GmudDoChamado({ chamado }: { chamado: DetalheChamado }) {
  if (!chamado.gmudId || !chamado.gmudCodigo) {
    return <span className="text-slate-500">Nenhuma</span>
  }
  return (
    <Link
      to={`/gmuds/${chamado.gmudId}`}
      className="font-medium text-niteroi-700 underline underline-offset-2 hover:text-niteroi-900"
    >
      {chamado.gmudCodigo}
    </Link>
  )
}
